// Shared logic for all calendar-v2 row components.

import { useEffect } from "react";
import type {
  CalendarDataCategoryResponse,
  CalendarEvent,
  CategoryHistory,
  Importance,
} from "../types";
import { getOutcomeClasses } from "../utils/outcomes";

export interface CommonRowProps {
  event: CalendarEvent;
  showLowHigh?: boolean;
  isChanged?: boolean;
  clearChangedStatus?: (uuid: string) => void;
  isNextEvent?: boolean;
  /** When "mini", data rows render a sparkline of the category's history. */
  chart?: "mini";
  /** Category history keyed by category id (for the mini-chart). */
  categoryHistory?: Record<string, CategoryHistory>;
}

/** Clear the `isChanged` pulse status after the animation finishes (~1s). */
export function useChangeAnimation(props: CommonRowProps): void {
  const { event, isChanged, clearChangedStatus } = props;
  const uuid = event.uuid;
  useEffect(() => {
    if (!isChanged || !uuid || !clearChangedStatus) return;
    const timer = setTimeout(() => clearChangedStatus(uuid), 1000);
    return () => clearTimeout(timer);
  }, [isChanged, uuid, clearChangedStatus]);
}

/** Category info, if present (only data events carry a category). */
export function getCategory(
  event: CalendarEvent
): CalendarDataCategoryResponse | undefined {
  return (event as { category?: CalendarDataCategoryResponse }).category;
}

/**
 * Effective importance: a per-event value (speaker/supply/earning) wins, falling
 * back to the linked category (data events).
 */
export function getImportance(event: CalendarEvent): Importance | undefined {
  return (
    (event as { importance?: Importance }).importance ??
    getCategory(event)?.importance
  );
}

/** Whether the event has expandable detail. */
export function hasDetail(event: CalendarEvent): boolean {
  const detail = (event as { detail?: string | null }).detail;
  return !!detail && detail.trim().length > 0;
}

/** Base row classes shared by every classification. */
export function computeRowClasses(props: CommonRowProps): string {
  const classes: string[] = [];

  if (props.isChanged) classes.push("row-pulsing");
  if (props.isNextEvent) classes.push("next-event");

  const importance = getImportance(props.event);
  if (importance === "high") classes.push("importance-high");
  else if (importance === "low") classes.push("importance-low");
  else classes.push("importance-medium");

  const dataSource = getCategory(props.event)?.data_source;
  if (dataSource === "TradingEconomics") classes.push("te-source");

  return classes.join(" ");
}

/** Data-row classes: base classes plus the actual-vs-expected outcome class. */
export function computeDataRowClasses(props: CommonRowProps): string {
  const base = computeRowClasses(props);
  const event = props.event as {
    actual?: string | null;
    expected?: string | null;
  };
  if ("actual" in props.event && "expected" in props.event) {
    const outcome = getOutcomeClasses(
      event.actual,
      event.expected,
      getCategory(props.event)?.inverted
    );
    return `${base} ${outcome}`;
  }
  return base;
}
